/**
 * Hợp đồng BE (F-MAP-03): `GET /api/alpha-earth/tiles` với `date=YYYY-MM-DD` (không dùng `year`).
 * Không gửi `z,x,y` → JSON `data.tiles` là template GEE. Gửi đủ `z,x,y` → `data.tiles` là URL một ô.
 */

export const ALPHA_EARTH_TILES_PATH = '/api/alpha-earth/tiles';

export interface AlphaEarthMetadataQuery {
  bbox: string;
  /** Ngày tham chiếu YYYY-MM-DD (mosaic theo năm dương lịch chứa ngày này — hợp đồng F-MAP-03). */
  date: string;
  index: string;
  /** `null` → không thêm query `bands` (BAIS2). */
  bands: string | null;
  palette?: string | null;
  /** When all three are set, BE typically returns data.tiles as a direct tile image URL. */
  z?: number;
  x?: number;
  y?: number;
}

/**
 * Dựng URL metadata Alpha Earth. `baseUrl` lấy từ `getAlphaEarthTilesBaseUrl()` trong `config/env`.
 * Khi truyền đủ `z`, `x`, `y`, BE thường trả `data.tiles` là URL trực tiếp một ô.
 */
export function buildAlphaEarthMetadataUrl(baseUrl: string, q: AlphaEarthMetadataQuery): string {
  const root = baseUrl.replace(/\/$/, '');
  const u = new URL(`${root}${ALPHA_EARTH_TILES_PATH}`);
  u.searchParams.set('bbox', q.bbox);
  u.searchParams.set('date', q.date);
  u.searchParams.set('index', q.index);
  if (q.bands != null && String(q.bands).trim() !== '') {
    u.searchParams.set('bands', String(q.bands).trim());
  }
  if (q.palette != null && String(q.palette).trim() !== '') {
    u.searchParams.set('palette', String(q.palette).trim());
  }
  if (q.z != null && q.x != null && q.y != null) {
    u.searchParams.set('z', String(q.z));
    u.searchParams.set('x', String(q.x));
    u.searchParams.set('y', String(q.y));
  }
  return u.toString();
}

export interface AlphaEarthTilesApiBody {
  statusCode?: number;
  message?: string;
  data?: {
    tiles?: string | string[] | null;
  } | null;
}

/**
 * Trích một chuỗi URL template hoặc URL trực tiếp từ body JSON BE.
 */
export function parseTilesUrlFromJson(json: unknown): string | null {
  if (!json || typeof json !== 'object') return null;
  const body = json as AlphaEarthTilesApiBody;
  if (body.statusCode !== 200 || !body.data?.tiles) return null;
  const t = body.data.tiles;
  if (typeof t === 'string' && t.trim()) return t.trim();
  if (Array.isArray(t) && t.length > 0 && typeof t[0] === 'string' && t[0].trim())
    return t[0].trim();
  return null;
}

/**
 * Chuẩn hoá URL GEE: nếu BE trả URL đã gắn số `.../tiles/z/x/y` thay vì placeholder, suy template `.../tiles/{z}/{x}/{y}`.
 */
export function normalizeGeeTileTemplate(url: string): string {
  const trimmed = url.trim();
  if (trimmed.includes('{z}') && trimmed.includes('{x}') && trimmed.includes('{y}')) {
    return trimmed;
  }
  const m = trimmed.match(/^(.*)\/tiles\/\d+\/\d+\/\d+(\?.*)?$/);
  if (m) return `${m[1]}/tiles/{z}/{x}/{y}${m[2] ?? ''}`;
  return trimmed;
}

/**
 * URL probe: thay placeholder bằng 0,0,0 để kiểm tra có ảnh không.
 */
export function getAlphaEarthProbeUrl(template: string): string | null {
  const t = normalizeGeeTileTemplate(template);
  if (!t.includes('{z}') || !t.includes('{x}') || !t.includes('{y}')) return null;
  return t.replace('{z}', '0').replace('{x}', '0').replace('{y}', '0');
}

export type AlphaEarthFetchFailureReason =
  | 'bad_response'
  | 'no_tiles'
  | 'no_probe_url'
  | 'probe_not_image';

/**
 * Gọi metadata BE (không z/x/y), lấy `data.tiles`, chuẩn hoá template GEE, probe một ô (0,0,0) phải trả ảnh.
 */
export async function fetchAndProbeAlphaEarthTemplate(
  metadataUrl: string,
): Promise<{ ok: true; template: string } | { ok: false; reason: AlphaEarthFetchFailureReason }> {
  let res: Response;
  try {
    res = await fetch(metadataUrl);
  } catch {
    return { ok: false, reason: 'bad_response' };
  }
  const json = await res.json().catch(() => null);
  if (!res.ok || !json) return { ok: false, reason: 'bad_response' };
  const raw = parseTilesUrlFromJson(json);
  if (!raw) return { ok: false, reason: 'no_tiles' };
  const template = normalizeGeeTileTemplate(raw);
  const probeUrl = getAlphaEarthProbeUrl(template);
  if (!probeUrl) return { ok: false, reason: 'no_probe_url' };
  try {
    const probeRes = await fetch(probeUrl);
    const ct = probeRes.headers.get('content-type') || '';
    if (!probeRes.ok || !ct.includes('image')) return { ok: false, reason: 'probe_not_image' };
  } catch {
    return { ok: false, reason: 'probe_not_image' };
  }
  return { ok: true, template };
}

